import React, { useState, useEffect, useRef, useContext } from "react";
import Config from '../utils/Config';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fireConfetti, scorePassword } from '../utils/functions';
import Spinner from '../utils/spinner';
import "./Login.css";
import './UpdatePassword.css';
import { AuthContext } from "../context/AuthContext";
import useTitle from '../hooks/useTitle';
import { useNavigate } from "react-router-dom";

const UpdatePassword = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const fflPasswordRef = useRef(null);
  const fflConfirmPasswordRef = useRef(null);

  const [messageClass, setMessageClass] = useState("warning hidden");
  const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false); // spinner flag
  const [passwordReset, setPasswordReset] = useState(false);   // success state

  const { user } = useContext(AuthContext);
  useTitle('Update Password');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const [formData, setFormData] = useState({
    currentPassword: "",
    password: "",
    confirmPassword: "",
  });

  const [passwordScore, setPasswordScore] = useState(0);

  const passwordsMatch =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword;

  const samePasswordMatch =
    formData.currentPassword.trim() !== "" &&
    formData.password.trim() !== "" &&
    formData.currentPassword.trim() === formData.password.trim();

  // ✅ Focus on first input field
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (passwordReset) {
      const timer = setTimeout(() => {
        setPasswordReset(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordReset]);

  // ✅ Helper to hard-reset UI after success
  const resetUI = () => {
    setFormData({ currentPassword: "", password: "", confirmPassword: "" });
    setPasswordScore(0);
    setShowPassword(false);
    setShowPasswordCurrent(false);
    setMessageClass("warning hidden");
    const msgEl = document.getElementById('message');
    if (msgEl) {
      msgEl.innerHTML = "";
      msgEl.style.display = "none";
    }
    // Clear any lingering input refs just in case
    if (fflPasswordRef.current) fflPasswordRef.current.value = "";
    if (fflConfirmPasswordRef.current) fflConfirmPasswordRef.current.value = "";
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.currentPassword.trim()) newErrors.currentPassword = "Current password is required.";
    if (!formData.password.trim()) newErrors.password = "Password is required.";
    if (!formData.confirmPassword.trim()) newErrors.confirmPassword = "Confirm password is required.";
    if (formData.password.length < 8) newErrors.password = "Password must be at least 8 characters long.";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match!";
    if (formData.password === formData.currentPassword) newErrors.confirmPassword = "New password can't be same as current password!";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === "password") {
      setPasswordScore(scorePassword(value));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    setUpdatePassword(true); // show spinner

    const message = document.getElementById('message');

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      let errorMsg = "";
      if (newErrors.currentPassword) errorMsg += `❌ ${newErrors.currentPassword} <br />`;
      if (newErrors.password) errorMsg += `❌ ${newErrors.password} <br />`;
      if (newErrors.confirmPassword) errorMsg += `❌ ${newErrors.confirmPassword} <br />`;

      if (message) {
        message.innerHTML = errorMsg;
        setMessageClass("warning");
        message.style.display = "block";
      }

      setUpdatePassword(false); // hide spinner on validation fail
      return;
    }

    try {
      const response = await fetch(
        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_UPDATE_PASSWORD}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user?.id,
            currentPassword: formData.currentPassword,
            password: formData.password,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // ✅ Success: flip success UI, reset everything cleanly
        setPasswordReset(true);
        fireConfetti();
        resetUI();
      } else {
        if (message) {
          message.innerHTML = `❌ ${data?.message || "Failed to update password"}`;
          setMessageClass("warning");
          message.style.display = "block";
        }
      }
    } catch (error) {
      if (message) {
        message.innerHTML = `❌ Unexpected error. Try again.`;
        setMessageClass("warning");
        message.style.display = "block";
      }
    } finally {
      setUpdatePassword(false); // hide spinner
    }
  };

  const isSubmitDisabled =
    formData.currentPassword.length < 8 ||
    formData.password.length < 8 ||
    formData.currentPassword.trim() === formData.password.trim() ||
    !passwordsMatch;

  return (
    <div className="login-container login-container-dashboard" id="update-password">
      <div className="login-box">
        {passwordReset ? (
          <>
            <h2>🎉 Password Updated Successfully!</h2>
            <p className="updatePasswordText">Your password has been successfully reset.</p>

            {/* Optional: or let them close and stay */}
            <div className="return-link-wrapper" style={{ marginTop: '0.5rem' }}>
              <button className="return-link" onClick={() => setPasswordReset(false)}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="dashboard-title">
              <span className="titleIcon"><FontAwesomeIcon icon={"lock"} /></span>
              &nbsp;&nbsp;Update Password
            </h2>

            <div className={messageClass} id="message" />

            <form onSubmit={handleRegister}>
              {/* help browsers associate creds */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={user?.email || ""}
                readOnly
                hidden
              />

              <div className="password-container">
                <input
                  type={showPasswordCurrent ? "text" : "password"}
                  name="currentPassword"
                  placeholder="Current Password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  autoComplete="current-password"
                  ref={inputRef}
                />
                <button
                  type="button"
                  className="toggle-password-update"
                  onClick={() => setShowPasswordCurrent(!showPasswordCurrent)}
                  tabIndex={-1}
                >
                  {showPasswordCurrent ? "🙈 Hide" : "👁️ Show"}
                </button>
              </div>

              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  ref={fflPasswordRef}
                />
                <button
                  type="button"
                  className="toggle-password-update"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? "🙈 Hide" : "👁️ Show"}
                </button>
              </div>

              {formData.password && (
                <>
                  <div className="strength-bar-container">
                    <div className={`strength-bar fill-${passwordScore}`} />
                  </div>
                  <p className="password-hint">
                    Use at least 8 characters with letters, numbers, and symbols.
                  </p>
                </>
              )}

              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                ref={fflConfirmPasswordRef}
              />

              {samePasswordMatch ? (
                <p className="password-match no-match spacer">
                  ❌ New password can't be same as current password
                </p>
              ) : formData.confirmPassword ? (
                <p className={`password-match ${passwordsMatch ? "match" : "no-match"}`}>
                  {passwordsMatch
                    ? "✅ New & confirm passwords match"
                    : "❌ New & confirm passwords do not match"}
                </p>
              ) : null}

              <button
                type="submit"
                className="login-button update-password-button"
                disabled={isSubmitDisabled}
              >
                Update Password
              </button>
            </form>
          </>
        )}
      </div>

      {/* Fullscreen spinner */}
      <Spinner show={updatePassword} />
    </div>
  );
};

export default UpdatePassword;